import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Media, breakpoints } from "../../../styles";
import { Modal } from "../../../components/Modal/Modal";
import { useAccount } from "wagmi";
import { formatUnits } from "@ethersproject/units";
import { BigNumber } from "@ethersproject/bignumber";
import toast, { Toaster } from "react-hot-toast";
import { CrabABI } from "../../../constants/ABI/CrabABI";
import { CRAB_ADDRESS } from "../../../constants/addresses";
import { Contract } from "@ethersproject/contracts";
import Accordion from "../../../components/Accordion/Accordion";
import crabIcon from '../../../assets/seasons/CRAB.svg'
import { Box } from "../../../components/Layout/Box";
import { useWidthBreakpoint } from "../../../hooks";
import { Content } from "../../../components/Layout/Content";
import { CRAB_DROP_API } from "../../../constants/urls";
import { formatDisplay } from "../../../utils/formatDisplay";
import { useEthersProvider, useEthersSigner } from '@/utils/wagmi-ethers-adapters/ethers';

export type TestData = {
  merkleRoot: string;
  tokenTotal: string;
  claims: ClaimData;
};

type ClaimData = {
  index: number[];
  amount: string[];
  proof: string[][];
};

export const ClaimCrab = () => {

  const [unclaimedSeasons, setUnclaimedSeasons] = useState<boolean[]>([])

  const [modalOpen, setModalOpen] = useState(false);
  const [claimData, setClaimData] = useState<ClaimData>({index: [], amount: [], proof: []});
  const [claimDisabled, setClaimDisabled] = useState(false)

  const { address : walletAddress, isConnected } = useAccount();
  const { data: signer } = useEthersSigner();
  const provider = useEthersProvider();

  const isMobile = useWidthBreakpoint(breakpoints.mobile);

  const crabSeasons = ['Season Zero', 'Season One', 'Season Two']

  const updateClaimData = async () => {

    const crabs = new Contract(CRAB_ADDRESS, CrabABI, provider)

    const claims = await crabs.viewClaims(walletAddress);

    const data = await fetch(CRAB_DROP_API + walletAddress).then((response) => response.json())

    const newUnclaimedSeasons = claims.map((claim: boolean, index: number) => {
        if(!claim){
            if(data[crabSeasons[index]] && Object.keys(data[crabSeasons[index]]).length > 0){
                return true
            }
            return false
        }
        return false
    })

    setUnclaimedSeasons(newUnclaimedSeasons)

  }

  useEffect(() => {
    isConnected && updateClaimData()
  }, [walletAddress, isConnected]);

  useEffect(() => {
    if (isConnected) {

      const newClaimData : ClaimData = {index: [], amount: [], proof: []}

      fetch(CRAB_DROP_API + walletAddress).then((response) => response.json()).then((data) => {
        for (let index = 0; index < unclaimedSeasons.length; index++) {

            const unclaimed = unclaimedSeasons[index];
            const seasonClaimData = data[crabSeasons[index]];
            if(seasonClaimData && Object.keys(seasonClaimData).length > 0){
                newClaimData.index.push(seasonClaimData.index);
                newClaimData.amount.push(seasonClaimData.amount);
                newClaimData.proof.push(unclaimed ? 
                    seasonClaimData.proof.map((proofElement: string) => `0x${proofElement.replace(/^0x/, "")}` as `0x${string}`) : 
                    []
                );
            } else {
                newClaimData.index.push(0);
                newClaimData.amount.push('0');
                newClaimData.proof.push([]);
            }
        }
        setClaimData(newClaimData)
      })
    }
  }, [unclaimedSeasons]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleClaim = async () => {
    const crabs = new Contract(CRAB_ADDRESS, CrabABI, signer!)
    setClaimDisabled(true)
    try {      
      crabs.claim(
        claimData.index.map((index) => BigNumber.from(index)),
        claimData.amount.map((amount) => BigNumber.from(amount)),
        claimData.proof
      ).then((response: any) => {
          toast.promise(response.wait(), {
            loading: "Claiming $CRAB",
            success: () => {
              updateClaimData()
              handleCloseModal();
              return "Claim success!";
            },
            error: (_) => {
              setClaimDisabled(false)
              return "Claim error!";
            },
          });
        })
        .catch(() => {
          console.error("Claim error!")
          setClaimDisabled(false)
        });
    } catch (error) {
      console.error("Error claiming CRAB:", error);
    }
  };

  if(unclaimedSeasons.length && unclaimedSeasons.includes(true)){
    return (
        <Content>  
            <Toaster/>
            <BannerContainer>
                <BannerLeft>
                    <TokenLogo
                        src={`../assets/seasons/CRAB.svg`}
                        style={{marginRight: '8px', width: '64px'}}
                    />
                    <BannerTextWrapper>
                        <BannerHeading>
                            You're eligible for <span style={{ color: '#DD5A35', margin: '0 4px' }}> $CRAB! </span>
                            </BannerHeading>
                        {!isMobile && 
                            <BannerSubText>
                                Crabs will be redeemable for <span style={{ color: '#00BDFF', margin: '0 4px' }}> $SHELL </span> at token launch
                            </BannerSubText>
                        }
                    </BannerTextWrapper>
                </BannerLeft>
                <Button onClick={handleOpenModal}>
                    <ButtonText>Claim {!isMobile && "Tokens"}</ButtonText>
                </Button>
            </BannerContainer>
            {modalOpen && (
                <StyledModal isVisible={modalOpen} onClose={handleCloseModal}>
                    <ModalTitleWrapper>
                    <Title>
                        You're eligible to claim $CRAB!
                    </Title>
                    </ModalTitleWrapper>
                    <TokenLogo
                        src={`../assets/seasons/CRAB.svg`}
                        style={{margin: '18px auto', width: '96px'}}
                    />
                    <Text style={{ marginBottom: "12px", fontSize: "16px" }}>
                    Breakdown
                    </Text>
                    {unclaimedSeasons.map((unclaimed, index) => 
                        <Accordion
                          key={index}
                          expandable={false}
                          title={crabSeasons[index]}
                          logo={`../assets/seasons/season${index}.png`}
                          tokenAmount={`${formatDisplay(formatUnits(BigNumber.from(claimData.amount[index])), 2)}`}
                          endIcon={crabIcon}
                          ineligible={unclaimed == false}
                        />
                    )}
                    <ModalTextWrapper>
                    <Text style={{ lineHeight: "26px" }}>
                        You'll receive
                        <br />
                        <ModalHighlightText>
                        {claimData &&
                        formatDisplay(claimData.amount.map(amount => parseFloat(formatUnits(BigNumber.from(amount))))
                            .filter((_, index) => unclaimedSeasons[index])
                                .reduce((partialSum, a) => partialSum + a, 0)
                            .toString(), 2)
                        }{<img src={`../assets/seasons/CRAB.svg`} style={{position: 'relative', top: '6px', width: '24px', margin: '0 4px'}}/>}
                        CRAB
                        </ModalHighlightText>
                    </Text>
                    </ModalTextWrapper>
                    <ModalButton onClick={handleClaim} disabled={claimDisabled}>Claim</ModalButton>
                </StyledModal>
            )}

        </Content>
    );
  } else {
    return null
  }
};

const BannerContainer = styled(Box)`
  align-items: center;
  background: #0a0e27;
  border-radius: 20px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  max-width: 100%;
  min-height: 90px;
  padding: 7px 24px 7px 16px;
  position: relative;
  width: 100%;

  ${Media.mobile} {
    margin-bottom: 10px;
    min-height: auto;
    padding: 10px;
  }
`;

const BannerSubText = styled.div`
  align-items: center;
  color: #7d7d97;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  height: auto;
  line-height: 136%;
  max-width: 431px;
  width: 100%;
  margin-top: 2px;

  ${Media.smallDesktop} {
    font-size: min(max(1.5vw, 12px), 14px);
  }

  ${Media.mobile} {
    flex-grow: 0;
    flex: none;
    font-size: min(max(1.5vw, 10px), 14px);
    order: 1;
  }
`;
const Button = styled.button`
  align-items: center;
  background-color: #0a0e27;
  border-radius: 16px;
  border: 1px solid #07c0fb;
  box-sizing: border-box;
  display: flex;
  filter: drop-shadow(0px 24px 84px #08081b);
  flex-direction: row;
  gap: 10px;
  height: 47px;
  justify-content: center;
  padding: min(max(1%, 10px), 14px) min(max(3%, 14px), 24px);
  position: relative;
  width: 180px;

  ${Media.tablet} {
    display: flex;
    justify-content: center;
    margin-right: 8px;
  }
`;

const ButtonText = styled.span`
  align-items: center;
  border: none;
  color: #ffffff;
  display: flex;
  font-family: "Inter";
  font-size: min(max(1.6vw, 12px), 16px);
  line-height: min(max(1.9vw, 14px), 19px);
  font-style: normal;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
`;

const BannerLeft = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  padding-right: 12px;

  ${Media.mobile} {
    justify-content: flex-start;
    align-items: center;
    width: 100%;
  }
`;

const BannerTextWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-basis: 0;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
`;

const BannerHeading = styled.div`
  align-items: center;
  color: #ffffff;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: min(max(2.5vw, 14px), 24px);
  font-style: normal;
  font-weight: 500;
  height: auto;
  letter-spacing: -0.03em;
  line-height: calc(29px + (24px - 20px) * (100vw - 600px) / (768px - 600px));

  max-width: 470px;
  width: 100%;

  ${Media.tablet} {
    font-size: calc(24px + (18px - 14px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    font-size: min(max(2.5vw, 13px), 24px);
    min-width: 162px;
  }
`;

const TokenLogo = styled.img`
  height: auto;
  margin-bottom: -8%;
  margin-top: -8%;
  width: 128px;

  ${Media.tablet} {
    width: calc(128px + (160px - 128px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    width: min(max(10vw, 64px), 160px);
  }
`;

const ModalTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ModalButton = styled.button`
  align-items: center;
  background: linear-gradient(90.44deg, #37dcf2 0.87%, #07c0fb 100%);
  border-radius: 16px;
  color: #0a0e27;
  display: flex;
  font-size: 18px;
  font-weight: 500;
  height: 64px;
  justify-content: center;
  line-height: 22px;
  margin-top: 40px;
  margin: 12px auto 24px;
  width: 180px;
  svg {
    color: #464659;
    height: 28px;
    margin-right: 8px;
    width: 28px;
  }

  &:not(:disabled) svg path {
    fill: #151530;
  }

  &:disabled {
    background: #0a0e27;
    border: solid 2px #464659;
    color: #464659;
  }

  ${Media.mobile} {
    margin-bottom: 156px;
  }
`;

const ModalTextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ModalHighlightText = styled.span`
  -webkit-background-clip: text;
  color: #DD5A35;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  margin: 0 4px;
  ${Media.mobile} {
    font-size: min(max(2vw, 20px), 26px);
  }
`;

const StyledModal = styled(Modal)`
  color: #00bdff;
  height: 672px;
  padding-bottom: 0;
  svg {
    height: 128px;
    margin: 24px auto 24px;
    width: 128px;
  }

  ${Media.mobile} {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    height: 95%;
    margin-top: 10%;
    max-width: 100%;
  }
`;

const Title = styled.h3`
  color: #ffffff;
  font-size: 26px;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 36px;
  margin-top: 12px;
  text-align: center;

  ${Media.tablet} {
    font-size: calc(30px + (26px - 30px) * (100vw - 600px) / (768px - 600px));
    line-height: calc(36px + (35px - 36px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    align-self: stretch;
    flex-grow: 0;
    flex: none;
    font-size: min(max(2.5vw, 26px), 26px);
    line-height: min(max(3vw, 35px), 35px);
    order: 0;
  }
`;

const Text = styled.p`
  color: #7d7d97;
  font-size: 18px;
  font-weight: 300;
  line-height: 24px;
  text-align: center;
`;
